import React, { useState } from 'react';

import { Box } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { useTranslation } from 'next-i18next';

import { GroupChannel } from '@sendbird/chat/groupChannel';

import SearchAllUsersAndGroupsModal from 'src/components/ui/SearchAllUsersAndGroupsModal';

import type { Props as ButtonProps } from 'src/components/ui/Button';
import Button from 'src/components/ui/Button';
import type { Props as IconProps, icons } from 'src/components/ui/Icon';
import Icon from 'src/components/ui/Icon';

import ConfirmModal from 'src/components/ui/ConfirmModal';
import useAlerts from 'src/hooks/useAlerts';
import useModalOpenState, { useShareAsPrivateMessageModal, useShareViaModal } from 'src/hooks/useModalOpenState';
import useUser from 'src/hooks/useUser';

import {
    useAddBlockedUserMutation,
    useAddContentEventMutation,
    useRemoveBlockedUserMutation,
} from 'src/hooks/__generated__/queries';
import type { Company, User } from 'src/types/__generated__/graphql';

import ReportModal from 'src/components/ui/ReportModal';
import Typography from 'src/components/ui/Typography';
import { routePathContactsWithChannelUrl, routePathContactsWithUserId } from 'src/constants/router';
import useActivityToDisplay from 'src/hooks/useActivityToDisplay';
import useAppRouter from 'src/hooks/useAppRouter';
import type { HeaderSectionProps } from 'src/components/profile/HeaderSection';
import { styledComponent } from 'src/utils/styled';
import { ExternalShareModal } from 'src/components/ad/ExternalShareModal';

import TinyPreviewCard from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import type { MenuOption } from 'src/types/MenuOption';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

const StyledButton = styledComponent(Button)`
    mind-width:0;
    & > span {
        text-transform: none;
    }
    & .MuiButton-iconSizeMedium {
        margin: 0;
    }
`;

type Props = {
    additionalOptions: Record<string, MenuOption>;
    user?: HeaderSectionProps['userProfile'];
    company?: Pick<Company, 'id' | 'name'>;
    refetchQueries: string[];
    buttonProps?: ButtonProps & { 'data-target-id'?: string };
};

const ProfilePlusButton: React.FC<Props> = ({ additionalOptions, user, refetchQueries, buttonProps }) => {
    const router = useAppRouter();

    const { addAlert } = useAlerts();
    const { t } = useTranslation(['profile']);
    const currentUser = useUser();
    const getActivityToDisplay = useActivityToDisplay();
    const [addContentEventMutation] = useAddContentEventMutation();
    const { logEventDynamic } = useDynamicLogEvents();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const { props: reportModalProps, open: openReportModal } = useModalOpenState();
    const { props: confirmModalProps, open: openconfirmModal } = useModalOpenState(false);
    const { props: shareModalProps, open: openShareModal } = useShareViaModal();
    const { props: searchContactModalProps, open: openSearchContactModal } = useShareAsPrivateMessageModal();

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.stopPropagation();
        setAnchorEl(null);
    };

    const combinedRefetchQueries = ['MyContacts', 'CurrentUserProfile', 'Followers', 'Following', ...refetchQueries];

    const [addBlockedUserMutation] = useAddBlockedUserMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
        onCompleted() {
            addAlert({
                type: 'success',
                message: t('profile:snackbarBlockUser', { name: user?.fullName }),
            });
        },
    });

    const [removeBlockedUserMutation] = useRemoveBlockedUserMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
        onCompleted() {
            addAlert({
                type: 'success',
                message: t('profile:snackbarUnblockUser', { name: user?.fullName }),
            });
        },
    });

    const handleAddBlockedContact = async () => {
        await addBlockedUserMutation({
            variables: {
                userId: user?.id as string,
            },
            refetchQueries: combinedRefetchQueries,
            awaitRefetchQueries: true,
        });
        logEventDynamic(AnalyticsEvent.Block);
    };

    const handleRemoveBlockedContact = async () => {
        await removeBlockedUserMutation({
            variables: {
                userId: user?.id as string,
            },
            refetchQueries: combinedRefetchQueries,
            awaitRefetchQueries: true,
        });
    };

    const onRecipientSelection = async (selectedRecipient: User | GroupChannel) => {
        const contactRoute =
            selectedRecipient instanceof GroupChannel
                ? `${routePathContactsWithChannelUrl}${selectedRecipient.url}`
                : `${routePathContactsWithUserId}${selectedRecipient.id}`;
        if (contactRoute) {
            await router.push(`${contactRoute}&sharedUserId=${user?.id}`);
            await addContentEventMutation({
                variables: {
                    postId: user?.id,
                    eventType: 'shared_internally',
                },
                fetchPolicy: 'no-cache',
            });
        }
    };

    const menuOptions: Record<string, MenuOption> = {
        ...additionalOptions,
        shareInPrivateMessage: {
            condition: Boolean(currentUser),
            iconName: 'ChatPrimaryInactive' as keyof typeof icons,
            handleClick: openSearchContactModal,
            mainTranslationKey: 'common:shareInPrivateMessage',
        },
        shareVia: {
            iconName: 'ExternalLink' as keyof typeof icons,
            handleClick: openShareModal,
            condition: true,
            mainTranslationKey: 'common:shareVia',
        },
        report: {
            iconName: 'Alert' as keyof typeof icons,
            mainTranslationKey: 'profile:report',
            condition: Boolean(currentUser && user?.id !== currentUser.id),
            handleClick: openReportModal,
        },
        block: {
            iconName: 'Prohibited' as keyof typeof icons,
            mainTranslationKey: 'profile:blockUser',
            condition: Boolean(currentUser && user?.id !== currentUser.id && !user?.isBlocked),
            handleClick: openconfirmModal,
        },
        unblock: {
            iconName: 'Prohibited' as keyof typeof icons,
            mainTranslationKey: 'profile:unblockUser',
            condition: Boolean(currentUser && user?.id !== currentUser.id && user?.isBlocked),
            handleClick: openconfirmModal,
        },
    };

    return (
        <>
            <StyledButton
                size="medium"
                variant="outlined"
                onClick={handleOpenMenu}
                sx={{ p: '0.5rem', 'min-width': 0, width: '2.5rem', height: '2.5rem' }}
                {...buttonProps}
                shrink
            >
                <Icon name="DotsHorizontalRounded" />
            </StyledButton>
            <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                onClick={handleCloseMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                keepMounted
            >
                {Object.entries(menuOptions).map(
                    ([optionName, option]) =>
                        option.condition && (
                            <MenuItem
                                key={option.mainTranslationKey}
                                onClick={option.handleClick}
                                sx={{ minHeight: 'auto' }}
                                {...(option.menuItemProps ?? {})}
                            >
                                <Icon name={option.iconName as IconProps['name']} size="1rem" />
                                <Box ml={1} data-target-id={`profileMoreButton-${optionName}`}>
                                    <Typography variant="h6">{t(option.mainTranslationKey)}</Typography>
                                </Box>
                            </MenuItem>
                        ),
                )}
            </Menu>
            {searchContactModalProps.open && (
                <SearchAllUsersAndGroupsModal
                    title={t('common:shareInPrivateMessage')}
                    topArea={
                        user && (
                            <Box display="flex" justifyContent="center" alignItems="center">
                                <TinyPreviewCard
                                    media={user.image ? [user.image] : undefined}
                                    subtitle={getActivityToDisplay(user)}
                                    title={user.fullName ?? undefined}
                                    type="user"
                                    user={user}
                                />
                            </Box>
                        )
                    }
                    submitLabel={t('ad:share')}
                    onSelection={onRecipientSelection}
                    renderGroupConversationButton
                    showConversations
                    {...searchContactModalProps}
                />
            )}

            {shareModalProps.open && user && (
                <ExternalShareModal
                    {...shareModalProps}
                    externalShareTitle={user.fullName ?? ''}
                    shareContentProps={{
                        type: 'user',
                        id: user.id,
                    }}
                />
            )}

            <ConfirmModal
                title={
                    !user?.isBlocked
                        ? t('profile:titleBlockUser', { name: user?.fullName })
                        : t('profile:titleUnblockUser', { name: user.fullName })
                }
                description={
                    !user?.isBlocked
                        ? t('profile:descriptionBlockUser', { name: user?.fullName })
                        : t('profile:descriptionUnblockUser', { name: user.fullName })
                }
                onClick={!user?.isBlocked ? handleAddBlockedContact : handleRemoveBlockedContact}
                confirm={t('common:modal.confirm')}
                {...confirmModalProps}
            />
            <ReportModal
                userId={user?.id}
                open={reportModalProps.open}
                onClose={() => {
                    reportModalProps.onClose();
                }}
            />
        </>
    );
};

export default ProfilePlusButton;
